/**
 * Question Set Management Zod schemas
 * Requirements: 7.3, 7.4, 7.5, 7.6 - 2-choice validation, 30-character limit
 * Requirement 49.7: Multi-language admin support
 */
import { z } from 'zod';

/**
 * Multi-language text fields schema (Requirement 49.7)
 * All translation fields are optional with 200 character limit for answer choices
 */
const multiLanguageAnswerChoiceSchema = {
  textEn: z.string().max(200, 'English text must be 200 characters or less').optional().or(z.literal('')),
  textId: z.string().max(200, 'Indonesian text must be 200 characters or less').optional().or(z.literal('')),
  textMn: z.string().max(200, 'Mongolian text must be 200 characters or less').optional().or(z.literal('')),
  textNe: z.string().max(200, 'Nepali text must be 200 characters or less').optional().or(z.literal('')),
  textVi: z.string().max(200, 'Vietnamese text must be 200 characters or less').optional().or(z.literal('')),
};

/**
 * Multi-language text fields schema for questions (Requirement 49.7)
 * All translation fields are optional with 500 character limit for questions
 */
const multiLanguageQuestionSchema = {
  textEn: z.string().max(500, 'English text must be 500 characters or less').optional().or(z.literal('')),
  textId: z.string().max(500, 'Indonesian text must be 500 characters or less').optional().or(z.literal('')),
  textMn: z.string().max(500, 'Mongolian text must be 500 characters or less').optional().or(z.literal('')),
  textNe: z.string().max(500, 'Nepali text must be 500 characters or less').optional().or(z.literal('')),
  textVi: z.string().max(500, 'Vietnamese text must be 500 characters or less').optional().or(z.literal('')),
};

// Answer choice schema with 200-character limit (multi-language support) + multi-language (Requirement 49.7)
export const answerChoiceSchema = z.object({
  id: z.string().optional(),
  text: z
    .string()
    .min(1, '選択肢テキストを入力してください')
    .max(200, '選択肢テキストは200文字以内で入力してください'),
  thinkingTypeCode: z
    .string()
    .min(1, '思考傾向コードを入力してください')
    .max(10, '思考傾向コードは10文字以内で入力してください'),
  order: z.number().int().min(0),
  ...multiLanguageAnswerChoiceSchema,
});

// Question schema requiring exactly 2 choices (Requirement 7.3) + multi-language (Requirement 49.7)
export const questionSchema = z.object({
  id: z.string().optional(),
  text: z.string().min(1, '質問文を入力してください'),
  order: z.number().int().min(0),
  answerChoices: z
    .array(answerChoiceSchema)
    .length(2, '各質問には正確に2つの選択肢が必要です'),
  ...multiLanguageQuestionSchema,
});

// Question set creation schema
export const createQuestionSetSchema = z.object({
  title: z
    .string()
    .min(1, 'タイトルを入力してください')
    .max(200, 'タイトルは200文字以内で入力してください'),
  description: z
    .string()
    .max(1000, '説明は1000文字以内で入力してください')
    .optional(),
  isPublished: z.boolean().optional(),
  questions: z
    .array(questionSchema)
    .min(1, '最低1つの質問が必要です'),
});

// Question set update schema
export const updateQuestionSetSchema = z.object({
  title: z
    .string()
    .min(1, 'タイトルを入力してください')
    .max(200, 'タイトルは200文字以内で入力してください')
    .optional(),
  description: z
    .string()
    .max(1000, '説明は1000文字以内で入力してください')
    .optional(),
  isPublished: z.boolean().optional(),
  questions: z
    .array(questionSchema)
    .min(1, '最低1つの質問が必要です')
    .optional(),
});

// Type exports
export type AnswerChoiceInput = z.infer<typeof answerChoiceSchema>;
export type QuestionInput = z.infer<typeof questionSchema>;
export type CreateQuestionSetInput = z.infer<typeof createQuestionSetSchema>;
export type UpdateQuestionSetInput = z.infer<typeof updateQuestionSetSchema>;
